// Formül yardımı içeriği — TEK kaynak (FormulaHelpPanel bunu render eder; kılavuz §11.1 ile
// elle eşlenik). Fonksiyon imzaları shared `kpi-formula.ts` ayrıştırıcısıyla hizalı.

export interface FormulaHelpItem {
  /** gösterilen imza — `TOPLA(kod)` */
  signature: string;
  title: string;
  description: string;
  /** tıklayınca ifadeye eklenir */
  example: string;
  exampleNote?: string;
  /** Excel çıktısındaki karşılığı */
  excel?: string;
}

export interface FormulaHelpSection {
  title: string;
  items: FormulaHelpItem[];
}

export const FORMULA_HELP_SECTIONS: readonly FormulaHelpSection[] = [
  {
    title: "Temel işlemler",
    items: [
      {
        signature: "a + b · a - b · a * b · a / b · ( )",
        title: "Dört işlem ve parantez",
        description:
          'Gösterge kodları ve sayılar dört işlemle birleştirilir; parantez önceliği belirler. Birden çok kalemi toplamak için + kullanılır (TOPLA değil). + ve - ile birleşen kalemlerde "Ölçülmüyor" işaretli kalem atlanır (kısmi toplam †, hücrede ⓘ hangi kalemin dahil olmadığını gösterir); boş kalem hesabı durdurur (HESAPLANAMADI). Çıkarmada taban (- işaretinin solu) ölçülmüyorsa sonuç hesaplanamaz; * ve / işlemlerinde Ölçülmüyor kalem atlanmaz.',
        example: "dogalgaz_tuketimi + dizel_tuketimi + benzin_tuketimi",
        excel: "=IF(ISNUMBER(B5),B5,0)+IF(ISNUMBER(B6),B6,0)+IF(ISNUMBER(B7),B7,0)",
      },
      {
        signature: "sayı",
        title: "Sabit sayı",
        description:
          "Ondalık ayırıcı NOKTA (1000000, 0.0036); binlik ayırıcı yazılmaz. Yüzde için 100 ile çarpın.",
        example: "kaza_sayisi * 1000000 / calisma_saati",
        exampleNote: "LTIFR — bir milyon çalışma saati başına kaza",
      },
    ],
  },
  {
    title: "Fonksiyonlar",
    items: [
      {
        signature: "TOPLA(kategori_kodu)",
        title: "Kategori toplamı",
        description:
          'Verilen kategorinin (ya da alt göstergeleri olan düğümün) altındaki TÜM göstergeleri toplar; kategoriye sonradan eklenen kalem kendiliğinden dahil olur. "Ölçülmüyor" işaretli kalem atlanır (kısmi toplam †); boş kalem hesabı durdurur (HESAPLANAMADI); hiçbir kalem ölçülmüyorsa sonuç HESAPLANAMADI. Tek argüman alır — seçili kalemleri toplamak için + kullanın.',
        example: "TOPLA(enerji_tuketimi_kalemleri)",
        excel: "=SUM(B5:B11)",
      },
      {
        signature: "ORT(kategori_kodu)",
        title: "Kategori ortalaması",
        description:
          'Kategorinin altındaki göstergelerin aritmetik ortalaması. "Ölçülmüyor" işaretli kalem atlanır, ortalama ölçülen kalemlerden alınır (kısmi †). Boş alt gösterge atlanmaz: biri bile boşsa (ya da kategoride gösterge yoksa) sonuç HESAPLANAMADI — TOPLA ile aynı kural.',
        example: "ORT(tesis_enerji_yogunlugu)",
        excel: "=AVERAGE(...)",
      },
      {
        signature: 'TOPLA_ALT() · TOPLA_ALT("boyut")',
        title: "Alt kalemlerin toplamı (kırılım)",
        description:
          'Göstergenin kendi alt kalemlerini toplar: hesap grubunun (ör. "Cinsiyete göre" › Kadın, Erkek) üyeleri. Gösterge kodu almaz; formül hangi göstergedeyse onun alt kalemleri okunur. TOPLA(kategori) ile farkı: TOPLA bir kategorinin altındaki göstergeleri toplar, TOPLA_ALT bir göstergenin kırılım kalemlerini — birden çok kırılım grubu varsa yalnız hesap grubu sayılır (çift sayım yok). Grup argümanı isteğe bağlıdır: TOPLA_ALT("cinsiyet") yazılırsa hesap grubu o grup olarak kaydedilir; grup bu veri setinde yoksa ya da "seçili kırılımlar" ise formül kaydedilmez. "Ölçülmüyor" işaretli kalem atlanır (kısmi toplam †); boş kalem hesabı durdurur (HESAPLANAMADI). Hesap grubu yoksa ya da beklenen kalemlerden biri veri setinde yoksa sonuç HESAPLANAMADI — eksik kalemle kısmi toplam yapılmaz.',
        example: "TOPLA_ALT()",
        exampleNote: 'belirli grup için: TOPLA_ALT("cinsiyet")',
        excel: "=SUM(B8:B9)",
      },
      {
        signature: 'ORT_ALT() · ORT_ALT("boyut")',
        title: "Alt kalemlerin ortalaması (kırılım)",
        description:
          'TOPLA_ALT ile aynı kalemlerin aritmetik ortalaması (ağırlıksız). "Ölçülmüyor" işaretli kalem atlanır, ortalama ölçülen kalemlerden alınır (kısmi †); boş kalem hesabı durdurur (HESAPLANAMADI). Oranlar için ağırlıklı ortalama gerekiyorsa AGIRLIKLI_ORT kullanın. Grup argümanı TOPLA_ALT ile aynı kuralla hesap grubunu belirler.',
        example: "ORT_ALT()",
        excel: "=AVERAGE(B8:B9)",
      },
      {
        signature: "AGIRLIKLI_ORT(kategori_kodu; agirlik_kategori_kodu)",
        title: "Ağırlıklı ortalama",
        description:
          'İki kategorinin alt kalemleri SIRAYLA eşlenir: değer kategorisinin 1. kalemi ağırlık kategorisinin 1. kalemiyle, 2.si 2.siyle… (ör. Ortalama maaş › Kadın maaş, Erkek maaş ↔ Çalışan sayısı › Kadın çalışan, Erkek çalışan). İki kategoride kalem sayısı eşit ve sıra aynı olmalı; eşit değilse formül kaydedilmez, sonradan bozulursa sonuç HESAPLANAMADI (gerekçe "alt kalem sayıları eşit değil"). Editör eşlemeyi formülün altında gösterir. Alt kalemlerin sırası iki kategoride aynı olmalı; veri seti editöründe eşleme önizlemesini kontrol edin (sayılar eşit kaldıkça kalem sırasını değiştirmek ya da yalnız bir taraftan kalem çıkarmak eşlemeyi sessizce kaydırır). Değeri ya da ağırlığı "Ölçülmüyor" olan çift atlanır (kısmi †); payda yalnız ölçülen çiftlerin ağırlığıdır. Boş kalem hesabı durdurur.',
        example: "AGIRLIKLI_ORT(ortalama_maas; calisan_sayisi)",
        exampleNote: "kalemler aynı sırada: kadın ↔ kadın, erkek ↔ erkek",
        excel:
          '=IF(ROWS(değerler)<>ROWS(ağırlıklar),"HESAPLANAMADI",SUMPRODUCT(değerler,ağırlıklar)/SUMIF(değerler,"<>*",ağırlıklar))',
      },
      {
        signature: "ONCEKI_YIL(kod)",
        title: "Önceki yılın değeri",
        description:
          'Aynı göstergenin bir önceki yıldaki değeri. Tablodaki ilk yılda (ya da önceki yıl boşsa ya da "Ölçülmüyor" ise) HESAPLANAMADI — toplama/çıkarmada da atlanmaz.',
        example: "elektrik_tuketimi - ONCEKI_YIL(elektrik_tuketimi)",
        exampleNote: "yıllık mutlak değişim",
      },
      {
        signature: "DEGISIM_YUZDE(kod)",
        title: "Yıllık % değişim",
        description:
          "(bu yıl − önceki yıl) / önceki yıl × 100. Önceki yıl sıfırsa HESAPLANAMADI. Bu göstergeler AI Kontrolü'nün sıçrama taramasından muaftır.",
        example: "DEGISIM_YUZDE(toplam_emisyon)",
      },
      {
        signature: 'CEVIR(kod; "hedef_birim")',
        title: "Birim çevrimi",
        description:
          'Göstergenin değerini kendi biriminden hedef birime çevirir (kWh→GJ, ton→kg, m3→lt, tCO2e→kgCO2e…). Göstergenin birimi tabloda yoksa üç argümanla kaynak birimi de verin: CEVIR(kod; "kWh"; "GJ"). Para birimi çevrilmez. Hangi çevrimlerin desteklendiği "Desteklenen çevrimler" tablosundadır; "Birim çevir (CEVIR) ekle" düğmesi parçayı sizin için yazar.',
        example: 'CEVIR(elektrik_kwh; "GJ")',
        excel: "=B5*0.0036",
      },
    ],
  },
  {
    title: "Birleşik örnekler",
    items: [
      {
        signature: "yoğunluk",
        title: "Üretim başına emisyon",
        description: "Toplam emisyonu üretime böler; payda sıfırsa HESAPLANAMADI.",
        example: "(kapsam1_emisyon + kapsam2_emisyon) / uretim_miktari",
      },
      {
        signature: "oran (%)",
        title: "Yenilenebilir enerji payı",
        description: "Oranı yüzde olarak vermek için 100 ile çarpın; göstergenin birimi % olmalı.",
        example: "yenilenebilir_enerji / TOPLA(enerji_tuketimi_kalemleri) * 100",
      },
      {
        signature: "kişi başı",
        title: "Çalışan başına eğitim saati",
        description: "İki ham göstergenin oranı.",
        example: "toplam_egitim_saati / toplam_calisan",
      },
    ],
  },
];

export const FORMULA_HELP_RULES: readonly string[] = [
  "Kodlar küçük harf, rakam ve alt çizgiden oluşur (katalogdaki kod); büyük harf yalnız fonksiyon adlarıdır. Yazarken öneri listesi açılır.",
  "Argüman ayırıcı noktalı virgüldür ( ; ) — virgül değil. Ondalık ayırıcı noktadır.",
  "Girdilerden biri boşsa ya da XXX ise sonuç HESAPLANAMADI / XXX olur; kısmi toplam yapılmaz. Danışman onayında (müşteri) girdi raporda XXX'e dönüşür.",
  'İstisna: "Ölçülmüyor" işaretli kalem TOPLA / ORT / AGIRLIKLI_ORT ve + / - toplamında atlanır; sonuç kısmi toplamdır (†) ve hücredeki ⓘ dahil edilmeyen kalemleri listeler. Hiçbir kalem ölçülmüyorsa sonuç HESAPLANAMADI (asla 0 değil).',
  "Bir formül kendi koduna ya da döngü oluşturan bir koda atıf yapamaz (a → b → a reddedilir). TOPLA_ALT() kendi alt kalemlerini okur — alt kalemlerden biri toplamı kullanırsa döngü olur.",
  'Grup argümanı çift tırnaklı boyut adıdır (TOPLA_ALT("cinsiyet")); yazıldığında göstergenin hesap grubunu belirler ve "Toplam nereden hesaplanır" seçimi formüle kilitlenir.',
  "Formül veri setine göre doğrulanır: veri setinde olmayan kod 'veri setinde yok' uyarısı verir; katalog formülünde tüm katalog kodları geçerlidir.",
  "Aynı göstergede birden çok varyant tanımlanabilir, yalnız biri aktiftir; müşteri portaldan başka bir varyantı talep edebilir.",
  'Excel çıktısında formüller canlı Excel formülüne çevrilir (SUM / AVERAGE / SUMPRODUCT / sabit çarpan); hesaplanamayan hücre IFERROR ile HESAPLANAMADI yazar. Ölçülmeyen hücre "Ölçülmedi" metniyle yazılır ve SUM / AVERAGE / SUMPRODUCT onu kendiliğinden atlar.',
];

// ─── TODO-KPI-154: TOPLA_ALT / ORT_ALT boyut çipleri ─────────────────────────────────────────────

export const FORMULA_DIMENSION_COPY = {
  title: "Alt kalem boyutları",
  intro:
    'TOPLA_ALT("boyut") / ORT_ALT("boyut") için boyut adları — tıklayınca TOPLA_ALT çağrısı ifadeye eklenir (ORT_ALT için fonksiyon adını değiştirin).',
  own: "Bu göstergenin grupları",
  custom: "Tanımladığınız boyutlar",
  chipTitle: (call: string) => `${call} ekle`,
} as const;

export interface FormulaDimensionChip {
  slug: string;
  label: string;
  /** ifadeye eklenen çağrı: `TOPLA_ALT("cinsiyet")` */
  call: string;
}

/**
 * Formül yardımı boyut çipleri: göstergenin kendi grupları (varsa) ve müşterinin tanımladığı özel
 * boyutlar. Aynı boyut iki bölümde tekrarlanmaz (gösterge grubu öncelikli); boş slug atlanır.
 */
export function formulaDimensionChips(
  own: readonly { slug: string; label: string }[],
  custom: readonly { slug: string; label: string }[],
): { own: FormulaDimensionChip[]; custom: FormulaDimensionChip[] } {
  const chip = (d: { slug: string; label: string }): FormulaDimensionChip => ({
    slug: d.slug,
    label: d.label,
    call: `TOPLA_ALT("${d.slug}")`,
  });
  const seen = new Set<string>();
  const take = (list: readonly { slug: string; label: string }[]): FormulaDimensionChip[] => {
    const out: FormulaDimensionChip[] = [];
    for (const d of list) {
      if (d.slug === "" || seen.has(d.slug)) continue;
      seen.add(d.slug);
      out.push(chip(d));
    }
    return out;
  };
  const ownChips = take(own);
  return { own: ownChips, custom: take(custom) };
}
